import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDot,
  ExternalLink,
  GitBranch,
  History,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { createSecretlintApi } from './api.js';
import type {
  SecretlintFinding,
  SecretlintRemoteProps,
  SecretlintRepository,
  SecretlintRun,
  SecretlintScanStatus,
} from './types.js';
import './styles.css';

/**
 * @graph-stack repository-secretlint
 * @graph-domain Engineering
 * @graph-business Exposed Repository Secretlint microfrontend app. Renders repository status, scan history, run details, masked findings, and manual rescans.
 * @graph-connects createSecretlintApi [calls] Secretlint API client
 */

/** @graph-connects none */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Module Federation exposed component.
 * @graph-connects SecretlintWorkspace [calls] Remote workspace component
 */
export function SecretlintApp(props: SecretlintRemoteProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <SecretlintWorkspace {...props} />
    </QueryClientProvider>
  );
}

/** @graph-connects SecretlintApp [references] Default remote export */
export default SecretlintApp;

/**
 * Main remote workspace.
 * @graph-connects /security/secretlint/* [calls] Secretlint API endpoints
 */
function SecretlintWorkspace({
  apiBaseUrl = '/api',
  initialPath = '/secretlint',
}: SecretlintRemoteProps) {
  const api = useMemo(() => createSecretlintApi(apiBaseUrl), [apiBaseUrl]);
  const initialRunId = extractRunId(initialPath);
  const [search, setSearch] = useState('');
  const [selectedRepositoryId, setSelectedRepositoryId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId);

  const repositories = useQuery({
    queryKey: ['secretlint', 'repositories'],
    queryFn: api.listRepositories,
  });

  const visibleRepositories = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (repositories.data?.repositories ?? []).filter((repository) =>
      query ? repository.fullName.toLowerCase().includes(query) : true,
    );
  }, [repositories.data?.repositories, search]);

  const selectedRepository =
    visibleRepositories.find((repository) => repository.id === selectedRepositoryId) ??
    visibleRepositories[0] ??
    null;

  const runs = useQuery({
    queryKey: ['secretlint', 'runs', selectedRepository?.id],
    queryFn: () => api.listRuns(selectedRepository?.id ?? ''),
    enabled: Boolean(selectedRepository?.id) && !selectedRunId,
  });

  const runDetail = useQuery({
    queryKey: ['secretlint', 'run', selectedRunId],
    queryFn: () => api.getRun(selectedRunId ?? ''),
    enabled: Boolean(selectedRunId),
  });

  const detailRun = runDetail.data?.run ?? runs.data?.runs[0] ?? null;
  const findings = runDetail.data?.findings ?? [];

  /** @graph-connects none */
  function selectRepository(repository: SecretlintRepository) {
    setSelectedRepositoryId(repository.id);
    setSelectedRunId(null);
    window.history.pushState(null, '', '/secretlint');
  }

  /** @graph-connects none */
  function selectRun(run: SecretlintRun) {
    setSelectedRunId(run.id);
    window.history.pushState(null, '', `/secretlint/runs/${encodeURIComponent(run.id)}`);
  }

  return (
    <main className="secretlint-shell">
      <section className="secretlint-toolbar">
        <div>
          <div className="secretlint-kicker">
            <ShieldAlert size={16} aria-hidden="true" />
            Secretlint
          </div>
          <h1>Repository Secret Scan</h1>
        </div>
        <div className="secretlint-search">
          <Search size={16} aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="repository を検索"
          />
        </div>
      </section>

      <section className="secretlint-summary-grid">
        <SummaryCard
          label="repositories"
          value={String(repositories.data?.repositories.length ?? 0)}
        />
        <SummaryCard
          label="findings"
          value={String(
            repositories.data?.repositories.reduce(
              (total, repository) => total + (repository.status?.latestFindingCount ?? 0),
              0,
            ) ?? 0,
          )}
        />
        <SummaryCard
          label="last completed"
          value={formatDate(getLatestCompletedAt(repositories.data?.repositories ?? []))}
        />
      </section>

      <section className="secretlint-grid">
        <div className="secretlint-panel">
          <div className="secretlint-panel-header">
            <h2>Repositories</h2>
          </div>
          {repositories.isLoading ? (
            <EmptyState label="repository status を読み込み中" />
          ) : repositories.isError ? (
            <EmptyState label="repository status を取得できません" />
          ) : (
            <div className="secretlint-repository-list">
              {visibleRepositories.map((repository) => (
                <button
                  key={repository.id}
                  type="button"
                  className={`secretlint-repository-row ${
                    repository.id === selectedRepository?.id ? 'is-active' : ''
                  }`}
                  onClick={() => {
                    selectRepository(repository);
                  }}
                >
                  <div>
                    <div className="secretlint-repo-name">{repository.fullName}</div>
                    <div className="secretlint-muted">
                      <GitBranch size={13} aria-hidden="true" />
                      {repository.defaultBranch}
                    </div>
                  </div>
                  <StatusPill status={repository.status?.latestStatus ?? null} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="secretlint-panel">
          <div className="secretlint-panel-header">
            <div>
              <h2>{selectedRepository?.fullName ?? 'Scan history'}</h2>
              {selectedRepository ? (
                <p>{selectedRepository.status?.latestFindingCount ?? 0} latest findings</p>
              ) : null}
            </div>
            {selectedRepository ? (
              <RescanButton
                api={api}
                repository={selectedRepository}
                onQueued={(run) => {
                  selectRun(run);
                }}
              />
            ) : null}
          </div>

          {!selectedRunId ? (
            <RunHistory
              isLoading={runs.isLoading}
              isError={runs.isError}
              runs={runs.data?.runs ?? []}
              onSelectRun={selectRun}
            />
          ) : (
            <RunDetail
              isLoading={runDetail.isLoading}
              isError={runDetail.isError}
              run={detailRun}
              findings={findings}
              onBack={() => {
                setSelectedRunId(null);
                window.history.pushState(null, '', '/secretlint');
              }}
            />
          )}
        </div>
      </section>
    </main>
  );
}

interface RescanButtonProps {
  api: ReturnType<typeof createSecretlintApi>;
  repository: SecretlintRepository;
  onQueued: (run: SecretlintRun) => void;
}

/**
 * Manual rescan action gated by API permission state.
 * @graph-connects /security/secretlint/repositories/:id/rescan [calls] Rescan API
 */
function RescanButton({ api, repository, onQueued }: RescanButtonProps) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.rescan(repository.id),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['secretlint'] });
      onQueued(response.run);
    },
  });

  return (
    <button
      type="button"
      className="secretlint-icon-button"
      disabled={!repository.canRescan || mutation.isPending}
      onClick={() => {
        mutation.mutate();
      }}
      title={repository.canRescan ? 'Rescan default branch' : '権限がありません'}
    >
      <RefreshCw size={16} aria-hidden="true" />
      <span>{mutation.isPending ? 'Queueing' : 'Rescan'}</span>
    </button>
  );
}

interface RunHistoryProps {
  isLoading: boolean;
  isError: boolean;
  runs: SecretlintRun[];
  onSelectRun: (run: SecretlintRun) => void;
}

/**
 * Render scan history for a repository.
 * @graph-connects none
 */
function RunHistory({ isLoading, isError, runs, onSelectRun }: RunHistoryProps) {
  if (isLoading) {
    return <EmptyState label="scan history を読み込み中" />;
  }
  if (isError) {
    return <EmptyState label="scan history を取得できません" />;
  }
  if (runs.length === 0) {
    return <EmptyState label="scan history がありません" />;
  }

  return (
    <div className="secretlint-run-list">
      {runs.map((run) => (
        <button
          key={run.id}
          type="button"
          className="secretlint-run-row"
          onClick={() => {
            onSelectRun(run);
          }}
        >
          <History size={17} aria-hidden="true" />
          <div>
            <div className="secretlint-run-title">
              {run.scope === 'pull_request' ? `PR #${String(run.pullRequestNumber)}` : run.ref}
            </div>
            <div className="secretlint-muted">{formatDate(run.createdAt)}</div>
          </div>
          <StatusPill status={run.status} />
          <strong>{run.findingCount}</strong>
        </button>
      ))}
    </div>
  );
}

interface RunDetailProps {
  isLoading: boolean;
  isError: boolean;
  run: SecretlintRun | null;
  findings: SecretlintFinding[];
  onBack: () => void;
}

/**
 * Render one scan run detail and masked findings.
 * @graph-connects none
 */
function RunDetail({ isLoading, isError, run, findings, onBack }: RunDetailProps) {
  if (isLoading) {
    return <EmptyState label="run detail を読み込み中" />;
  }
  if (isError || !run) {
    return <EmptyState label="run detail を取得できません" />;
  }

  return (
    <div>
      <button type="button" className="secretlint-link-button" onClick={onBack}>
        scan history に戻る
      </button>
      <div className="secretlint-detail-header">
        <div>
          <h3>{run.repositoryFullName}</h3>
          <p>
            {run.scope} / {run.ref} / {formatDate(run.completedAt ?? run.updatedAt)}
          </p>
        </div>
        <div className="secretlint-detail-actions">
          <StatusPill status={run.status} />
          {run.checkRunUrl ? (
            <a
              href={run.checkRunUrl}
              target="_blank"
              rel="noreferrer"
              className="secretlint-external"
            >
              Check <ExternalLink size={14} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>
      {run.errorMessage ? <div className="secretlint-alert">{run.errorMessage}</div> : null}
      {run.skippedReason ? <div className="secretlint-alert">{run.skippedReason}</div> : null}
      <FindingTable findings={findings} />
    </div>
  );
}

/**
 * Render masked finding rows.
 * @graph-connects none
 */
function FindingTable({ findings }: { findings: SecretlintFinding[] }) {
  if (findings.length === 0) {
    return <EmptyState label="検出はありません" />;
  }

  return (
    <div className="secretlint-table-wrap">
      <table className="secretlint-table">
        <thead>
          <tr>
            <th>file</th>
            <th>rule</th>
            <th>excerpt</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((finding) => (
            <tr key={finding.id}>
              <td>
                <code>
                  {finding.path}:{finding.line}:{finding.column}
                </code>
              </td>
              <td>{finding.ruleId}</td>
              <td>
                <code>{finding.maskedExcerpt ?? '--'}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render a status pill.
 * @graph-connects none
 */
function StatusPill({ status }: { status: SecretlintScanStatus | null }) {
  const tone = status ?? 'unknown';
  const Icon =
    status === 'completed' ? CheckCircle2 : status === 'error' ? AlertTriangle : CircleDot;
  return (
    <span className={`secretlint-status ${tone}`}>
      <Icon size={13} aria-hidden="true" />
      {status ?? 'none'}
    </span>
  );
}

/**
 * Render a KPI card.
 * @graph-connects none
 */
function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="secretlint-summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/**
 * Render empty and loading states.
 * @graph-connects none
 */
function EmptyState({ label }: { label: string }) {
  return <div className="secretlint-empty">{label}</div>;
}

/** @graph-connects none */
function extractRunId(path: string): string | null {
  const match = path.match(/\/secretlint\/runs\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** @graph-connects none */
function getLatestCompletedAt(repositories: SecretlintRepository[]): string | null {
  const completedAt = repositories
    .map((repository) => repository.status?.latestCompletedAt)
    .filter((value): value is string => Boolean(value))
    .sort();
  return completedAt.pop() ?? null;
}

/** @graph-connects none */
function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '--';
  }
  return new Intl.DateTimeFormat('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
